export const UDV_VALUE_ONLY_KEY_PREFIX = "__oneteg_udv_value_only__:";

export function isUdvValueOnlyKey(key: unknown): boolean {
  return typeof key === "string" && key.startsWith(UDV_VALUE_ONLY_KEY_PREFIX);
}

export function createUdvValueOnlyKey(): string {
  return `${UDV_VALUE_ONLY_KEY_PREFIX}${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

export function isEmptyUdvValue(value: unknown): boolean {
  return (
    value === "" ||
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function getUdvChangeMapEntryKey(item: {
  itemKey?: string;
  itemValue?: unknown;
  valueOnlyKey?: string;
}): string | undefined {
  const key =
    typeof item.itemKey === "string" && item.itemKey.startsWith("/#")
      ? item.itemKey.substring(2)
      : item.itemKey;

  if (key && key.trim() !== "") {
    delete item.valueOnlyKey;
    return key;
  }

  if (isEmptyUdvValue(item.itemValue)) {
    delete item.valueOnlyKey;
    return undefined;
  }

  if (!isUdvValueOnlyKey(item.valueOnlyKey)) {
    item.valueOnlyKey = createUdvValueOnlyKey();
  }

  return item.valueOnlyKey;
}

export function hasUdvValueOnlyRow(rows: unknown): boolean {
  if (!Array.isArray(rows)) {
    return false;
  }

  return rows.some((item: any) => {
    const key =
      typeof item?.itemKey === "string" && item.itemKey.startsWith("/#")
        ? item.itemKey.substring(2)
        : item?.itemKey;

    return (!key || key.trim() === "") && !isEmptyUdvValue(item?.itemValue);
  });
}

function stripValueOnlyKeys(map: unknown): unknown {
  if (!map || typeof map !== "object") {
    return map;
  }

  return Object.entries(map as Record<string, unknown>).reduce(
    (acc, [key, value]) => {
      if (!isUdvValueOnlyKey(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
}

export function stripUdvValueOnlyRows<T>(steps: T[]): T[] {
  if (!Array.isArray(steps)) {
    return steps;
  }

  return steps.map((step: any) => {
    const nextStep = { ...step };

    if (nextStep.udvChangeMapBefore !== undefined) {
      nextStep.udvChangeMapBefore = stripValueOnlyKeys(
        nextStep.udvChangeMapBefore
      );
    }
    if (nextStep.udvChangeMapAfter !== undefined) {
      nextStep.udvChangeMapAfter = stripValueOnlyKeys(
        nextStep.udvChangeMapAfter
      );
    }

    if (Array.isArray(nextStep.steps)) {
      nextStep.steps = stripUdvValueOnlyRows(nextStep.steps);
    }

    if (Array.isArray(nextStep.routes)) {
      nextStep.routes = nextStep.routes.map((route: any) =>
        Array.isArray(route?.steps)
          ? { ...route, steps: stripUdvValueOnlyRows(route.steps) }
          : route
      );
    }

    return nextStep;
  });
}

function getStepIdentity(step: any): string | undefined {
  const id = step?.origId ?? step?.id ?? step?.uniqueId;
  return id === undefined || id === null ? undefined : String(id);
}

function cloneMap(map: unknown): unknown {
  if (!map || typeof map !== "object") {
    return map;
  }

  try {
    return JSON.parse(JSON.stringify(map));
  } catch {
    return map;
  }
}

function collectUdvMaps(
  steps: unknown,
  result: Map<string, { before?: unknown; after?: unknown }>
): void {
  if (!Array.isArray(steps)) {
    return;
  }

  steps.forEach((step: any) => {
    const id = getStepIdentity(step);
    if (
      id &&
      (step.udvChangeMapBefore !== undefined ||
        step.udvChangeMapAfter !== undefined)
    ) {
      result.set(id, {
        before: step.udvChangeMapBefore,
        after: step.udvChangeMapAfter,
      });
    }

    collectUdvMaps(step?.steps, result);

    if (Array.isArray(step?.routes)) {
      step.routes.forEach((route: any) => collectUdvMaps(route?.steps, result));
    }
  });
}

export function preserveUdvChangeMaps<T>(
  incomingSteps: T[],
  currentSteps: unknown
): T[] {
  if (!Array.isArray(incomingSteps)) {
    return incomingSteps;
  }

  const currentUdvMaps = new Map<
    string,
    { before?: unknown; after?: unknown }
  >();
  collectUdvMaps(currentSteps, currentUdvMaps);

  const preserveList = (steps: any[]): any[] =>
    steps.map((step) => {
      const nextStep = { ...step };
      const id = getStepIdentity(nextStep);
      const current = id ? currentUdvMaps.get(id) : undefined;

      if (current?.before !== undefined) {
        nextStep.udvChangeMapBefore = cloneMap(current.before);
      }
      if (current?.after !== undefined) {
        nextStep.udvChangeMapAfter = cloneMap(current.after);
      }

      if (Array.isArray(nextStep.steps)) {
        nextStep.steps = preserveList(nextStep.steps);
      }

      if (Array.isArray(nextStep.routes)) {
        nextStep.routes = nextStep.routes.map((route: any) =>
          Array.isArray(route?.steps)
            ? { ...route, steps: preserveList(route.steps) }
            : route
        );
      }

      return nextStep;
    });

  return preserveList(incomingSteps);
}
